import { motion } from "framer-motion";
import { HelpCircle } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQS = [
  {
    q: "What should I do if my AC is not cooling?",
    a: "First, make sure the thermostat is set to cool and the breaker hasn’t tripped. Check that the air filter isn’t completely clogged and that the outdoor unit is running. If the system is still blowing warm air after 15–20 minutes, shut it off to avoid further damage and submit a service request — the more detail you can share (when it started, any sounds, ice on the lines), the faster the contractor can diagnose it.",
  },
  {
    q: "How often should I schedule HVAC maintenance in Florida?",
    a: "For Southwest Florida’s heat and humidity, twice a year is the practical answer: a pre-summer cooling tune-up and a quick fall check. Filters get changed every 60–90 days for most homes, and condensate drain lines should be flushed regularly to avoid backups during peak season.",
  },
  {
    q: "When should I repair vs replace my AC system?",
    a: "Generally, if your system is under 10 years old and the repair is a fraction of replacement cost, repair wins. Past 12–15 years — especially if you’ve had multiple service calls, rising energy bills, or refrigerant changes affecting your unit — replacement usually pays off in efficiency and reliability. A good contractor will lay out both numbers honestly.",
  },
  {
    q: "Can I request service online?",
    a: "Yes. This site’s smart intake walks you through what’s happening, your urgency, system details, and any photos that help. You’ll get a confirmation and a clear next step — no phone tag required.",
  },
  {
    q: "What information should I provide when requesting AC repair?",
    a: "Your name and best phone number, the city you’re in, what the system is doing (or not doing), how long it’s been going on, the approximate age of the system, whether it’s still cooling at all, and any unusual sounds, smells, or leaks. A photo of the indoor or outdoor unit is a bonus.",
  },
  {
    q: "Is this a real HVAC company?",
    a: "Sun Chaser Cooling is a sample HVAC website created by PrismForge Digital to demonstrate website, intake, and automation systems for HVAC and home service businesses.",
  },
  {
    q: "Who built this demo site?",
    a: 'This site was designed and built by PrismForge Digital — a studio that builds modern websites, smart lead intake, missed-call follow-up, AI receptionist concepts, and CRM-ready workflows for HVAC and home service businesses. You can learn more at <a class="text-aqua-deep underline-offset-2 hover:underline" href="https://prismforgedigital.com" target="_blank" rel="noreferrer">prismforgedigital.com</a>.',
  },
];

export default function FAQ() {
  return (
    <section
      data-testid="faq-section"
      aria-labelledby="faq-heading"
      className="relative bg-white py-20 sm:py-24"
    >
      <div className="container-page grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-14">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="lg:col-span-4"
        >
          <span className="pill">
            <HelpCircle className="h-3 w-3 text-aqua" />
            FAQ
          </span>
          <h2
            id="faq-heading"
            className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl lg:text-[2.5rem]"
          >
            Common questions.
          </h2>
          <p className="mt-4 max-w-md text-base text-navy/70">
            A few things homeowners ask before requesting service &mdash; plus
            a couple about this demo site itself.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.55, ease: "easeOut", delay: 0.05 }}
          className="lg:col-span-8"
          data-testid="faq-accordion-wrapper"
        >
          <Accordion
            type="single"
            collapsible
            defaultValue="faq-0"
            className="rounded-2xl border border-mist-line bg-white shadow-soft"
          >
            {FAQS.map((item, i) => (
              <AccordionItem
                key={item.q}
                value={`faq-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-mist-line px-5 last:border-b-0"
              >
                <AccordionTrigger className="py-5 text-left font-display text-base font-semibold text-navy hover:no-underline sm:text-lg">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 pr-6 text-[15px] leading-relaxed text-navy/75">
                  <span dangerouslySetInnerHTML={{ __html: item.a }} />
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>
      </div>
    </section>
  );
}
